/**
 * Binding: loads the Rust compute-scheduler WebAssembly package and describes the classes it
 * exports. All scheduling policy lives in `crates/compute-scheduler`; this file only fetches it.
 *
 * The package is built by `bun run build:compute-wasm` into
 * `crates/compute-scheduler/pkg-browser/` and copied by Vite to `/compute/`, the same way the
 * energy and scheduler packages are shipped. It is loaded with a runtime URL import (not bundled)
 * so a build without the package still works: the scheduler then runs jobs directly (see
 * `scheduler.ts`, "passthrough").
 *
 * @module apps/web/src/lib/compute/wasm
 */

/** Rust `ComputeCore` (crates/compute-scheduler/src/browser.rs). */
export interface ComputeCoreHandle {
  /** Submit a JSON job spec; returns the id that will answer the caller (known id = joined). */
  submit(specJson: string): number;
  /** Stop a job with an abort reason code (1 cancelled, 2 superseded, 3 deadline, 4 reset). */
  cancel(id: number, reason: number): void;
  /** Reset every live job named `name`, or all jobs. */
  cancelAll(name?: string): void;
  /** Report that a job's work finished. */
  complete(id: number, ok: boolean): void;
  /** Set a resource group's concurrency limit. */
  setGroupLimit(group: string, limit: number): void;
  /** Drain actions as flat `(kind, id, arg)` triples. */
  takeActions(): Float64Array;
  /** Current back-off code of a job's lane (0 = none). */
  backoffFor(id: number): number;
  /** Record an interaction now. */
  noteInput(): void;
  /** Whether a chunked loop should yield after `sliceElapsedMs`. */
  shouldYield(sliceElapsedMs: number): boolean;
  /** Telemetry snapshot JSON. */
  snapshot(): string;
  /** Budget and configuration JSON. */
  policy(): string;
  /** Remove listeners and timers, reset jobs. */
  dispose(): void;
}

/** Rust `PoolPolicy`. */
export interface PoolPolicyHandle {
  /** Warm Worker id to reuse, or -1 for a cold start. */
  acquire(): number;
  /** Offer back an unused Worker; false means terminate it. */
  release(id: number): boolean;
  /** An acquired Worker ended; a spare may be warmed again. */
  ownerEnded(): void;
  /** Acquired Workers still alive. */
  ownedCount(): number;
  /** Number of warm-ups to start now (0 while an acquired Worker is alive). */
  beginWarm(): number;
  /** A warm-up ended; whether to keep the Worker. */
  warmFinished(id: number, ok: boolean): boolean;
  /** Ids of expired idle Workers. */
  expire(): Uint32Array;
  /** Milliseconds until the next expiry. */
  msUntilExpiry(): number | undefined;
  /** Ids of all idle Workers. */
  clear(): Uint32Array;
  /** Idle Worker count. */
  idleCount(): number;
  /** Counters JSON. */
  stats(): string;
}

/** Rust `PerfLog`. */
export interface PerfLogHandle {
  /** Record a long task. */
  addLongTask(start: number, duration: number): void;
  /** Record a long animation frame with newline-separated script URLs and their durations. */
  addFrame(
    start: number,
    duration: number,
    blocking: number,
    scriptUrls: string,
    scriptDurations: Float64Array
  ): void;
  /** Record an Event Timing entry. */
  addEvent(interactionId: number, kind: string, start: number, duration: number): void;
  /** Summary JSON since a time. */
  summary(since: number): string;
  /** Long animation frames JSON. */
  frames(): string;
  /** `browser-responsiveness` benchmark JSON. */
  benchmark(since: number, scenario: string, core: ComputeCoreHandle): string;
  /** Forget everything. */
  clear(): void;
}

/** The initialized package. */
export interface ComputeModule {
  /** Scheduler core constructor. */
  ComputeCore: new (
    onChange: () => void,
    backgroundStartDelayMs?: number,
    inputQuietMs?: number
  ) => ComputeCoreHandle;
  /** Pool policy constructor. */
  PoolPolicy: new (maxIdle?: number) => PoolPolicyHandle;
  /** Responsiveness log constructor. */
  PerfLog: new (pageOrigin: string) => PerfLogHandle;
  /** Policy constants JSON. */
  policyConstants(): string;
}

/** Generated wasm-bindgen wrapper exports. */
type GeneratedModule = ComputeModule & {
  /** Initialize the WASM instance. */
  default: (options?: unknown) => Promise<unknown>;
};

/**
 * Fetch and initialize `/compute/fluxability_compute_scheduler.js`. Resolves null outside a
 * browser page or when the package is missing, so callers fall back instead of failing.
 */
export async function loadComputeModule(): Promise<ComputeModule | null> {
  if (typeof location === 'undefined' || typeof WebAssembly === 'undefined') return null;
  try {
    const url = new URL('/compute/fluxability_compute_scheduler.js', location.origin).href;
    const module: GeneratedModule = await import(/* @vite-ignore */ url);
    await module.default({
      module_or_path: new URL('/compute/fluxability_compute_scheduler_bg.wasm', location.origin)
        .href,
    });
    return typeof module.ComputeCore === 'function' ? module : null;
  } catch {
    return null;
  }
}
